/**
 * The ranges the admin app shows and their points (ADR 0016): 30 days by day, 90 days by week,
 * 12 months and everything by month. Core aggregates; the admin app only draws.
 */

export const RANGES = ["30d", "90d", "12m", "all"] as const;
export type Range = (typeof RANGES)[number];
export type Granularity = "day" | "week" | "month";

const DAY = 86_400_000;

export const addDays = (day: string, days: number) =>
  new Date(Date.parse(`${day}T00:00:00Z`) + days * DAY).toISOString().slice(0, 10);

export function granularityOf(range: Range): Granularity {
  return range === "30d" ? "day" : range === "90d" ? "week" : "month";
}

/** The first day a range covers up to `today`, or null for everything. */
export function rangeStart(range: Range, today: string): string | null {
  if (range === "30d") return addDays(today, -29);
  if (range === "90d") return addDays(today, -89);
  if (range === "12m") {
    const [year = 0, month = 1] = today.split("-").map(Number);
    const start = new Date(Date.UTC(year, month - 1 - 11, 1));
    return start.toISOString().slice(0, 10);
  }
  return null;
}

/** The period a day belongs to: the day, the Monday of its week, or "YYYY-MM". */
export function periodOf(day: string, granularity: Granularity): string {
  if (granularity === "day") return day;
  if (granularity === "month") return day.slice(0, 7);
  const weekday = (new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7;
  return addDays(day, -weekday);
}

/** Every period from `start` to `today`, oldest first, so a chart has no holes. */
export function periodsBetween(start: string, today: string, granularity: Granularity): string[] {
  const periods: string[] = [];
  for (let day = start; day <= today; day = addDays(day, 1)) {
    const period = periodOf(day, granularity);
    if (periods.at(-1) !== period) periods.push(period);
  }
  return periods;
}
